<template>
<div class="chapter">
<div class="inner_page">
<h1 class="cn"><a href="#/BENEATHTHESKIN_toc#chap-3">T<span class="small">HREE</span></a></h1>
<p class="img"><img alt="art" src="images/Art_common.jpg"/></p>
<p class="cotx"><span class="dropcap">I</span> was woken by the doorbell. I thought at first it must be some sort of joke or a wino who had mistaken the street door for
         the entrance to a hostel. I opened the curtains slightly in the front room and pushed my face against the glass, trying to
         see who it was, but the angle was wrong. I looked at my watch. Just after seven. I couldn’t think of anyone who could possibly
         be calling at this time. I wasn’t wearing anything so I pulled on a bright yellow plastic raincoat before going downstairs.
      </p>
<p>I opened the door just a fraction. The street door of the building opens directly onto Holloway Road, and I didn’t want to
         stop the traffic with my appearance just after I’ve woken up. It was the postman and my heart sank. When the postman wants
         to hand his mail to you personally, this is not generally good news. He usually wants you to sign for something in order to
         prove that you have received a horrible bill printed in red threatening to cut off your phone.
      </p>
<p>But he looked happy enough. Behind him I could see the beginnings of a day that was still cool but was going to be very hot
         indeed. I’d never seen this particular postman before, so I don’t know if it was a new thing, but he was wearing rather fetching
         blue serge shorts and a crisp light blue short-sleeved shirt. They were obviously official summer issue, but they looked jaunty.
         He wasn’t exactly young, but there was a <em>Baywatch</em>-postman air to him. So I stood on the doorstep looking at him with interest, and he looked back at me with some curiosity
         as well. I realized that my raincoat was on the skimpy side and not joined very well in the middle. I pulled it tightly together,
         which probably made things worse. This was starting to feel like a scene from one of those sleazy British comic sex films
         from the early seventies that you sometimes see on TV on Friday nights after you’ve got back from the pub. Porn for sad bastards.
      </p>
<p>“Flat C?” he asked.</p>
<p>“Yes.”</p>
<p>“There’s mail for you,” he said. “It wouldn’t go through the box.”</p>
<p>And there was. Lots and lots of different envelopes arranged in piles held together with elastic. Was this a joke? It took
         some complicated maneuvering to receive these bundles with one arm while holding my coat closed with the other.
      </p>
<p>“Happy birthday, is it?” he said with a wink.</p>
<p>“No,” I said, and pushed the door shut with a naked foot.</p>
<p>I took them upstairs and spilled them onto the table in the main room. I picked a dainty lilac envelope to open but I already
         knew what they were. One of the things about having a great-grandfather or great-great-grandfather who walked out of Armenia
         about a hundred years ago with nothing but a recipe for yogurt is that you’re very easy to look up in the phone book. Why
         couldn’t he have changed his name like other immigrants? I read the letter.
      </p>
<p class="ext1"><em>Dear Zoe Haratounian,</em></p>
<p class="ext2"><em>I read of your heroic exploit in this morning’s newspaper. First may I be allowed to congratulate you on your courage that
            you showed in tackling that person. If I may trespass a little longer on your patience…</em></p>
<p class="p1">I looked ahead and then over the page and then the page after that. There were five of them, and Janet Eagleton (Mrs.) had
         written on both sides of the paper in green ink. I’d save that one for later. I opened an envelope that looked more normal.
      </p>
<p class="ext1"><em>Dear Zoe,</em></p>
<p class="ext2"><em>Congratulations. You did brilliantly, and if more people behaved the way you did, London would be a better place to live.
            I also thought you looked lovely in the photograph in the paper and that’s really why I’m writing. My name is James Gunter
            and I’m twenty-five and I think I’m quite presentable looking, but I’ve always had trouble meeting the right girl, Miss “Right,”
            if you will….</em></p>
<p class="p1">I folded up the letter and placed it on top of Mrs. Eagleton’s. Another letter was more like a package. I opened it up. There
         was a bundle of paper half folded, half rolled up. I saw diagrams, arrows, subjects arranged in columns. But sure enough,
         on the first page it began as a letter addressed to me.
      </p>
<p class="ext1"><em>Dear Ms. Haroutunian,</em></p>
<p class="ext5"><em>(That’s an interesting name. Might you be a Zoroastrian? You can let me know at my box number (below). I will return to this
            subject (Zoroaster) below).</em></p>
<p class="ext3"><em>You have defenses against forces of darkness. But as you know there are other forces that are not so easily resisted. Do you
            know what a kunderbuffer is? If you do you can skip the following and begin at a section I will mark for your convenience
            with an asterisk. I append one for demonstration purposes <em>(*).</em> The section I will mark for your convenience I will mark with two (2) asterisks in order to avoid unnecessary confusion.</em></p>
<p class="p1">I put the letter on top of James Gunter’s. I went into the bathroom and washed my hands. That wasn’t enough. I needed a shower.
         That was always a bugger in my flat. I liked showers with frosted doors that you could stand up in. I once went out with someone
         whose only redeeming quality, in retrospect, was that he had a power shower with six different nozzles apart from the normal
         one above. But the shower in my flat involved squatting in the bath and fiddling with decaying valves and twisting the cable.
         Still, I lay back for several minutes with a washcloth over my face, showering it. It was like lying under a warm wet blanket.
      </p>
<p>I got out and got dressed in my work clothes. I made a mug of coffee and lit a cigarette. I felt a bit better. What <em>really</em> would have made me feel better is if the pile of letters had gone, but it was still stolidly present on the table. All those
         people knew where I lived. Well, not quite all. Another brisk inspection of the letters showed that several of them had been
         redirected from the newspapers where they had been originally sent. Some of them were probably nice. And at least, I thought
         to myself, they were writing instead of phoning up or calling round.
      </p>
<p>At that moment the phone rang, which made me jump. It wasn’t a fan. It was Guy, the real estate agent who was allegedly trying
         to sell my flat.
      </p>
<p>“I’ve got a couple of people who want a look around the property.”</p>
<p>“Fine,” I said. “You’ve got the key. What about that couple who saw it on Monday? What did they think?” I had no hopes of
         them, really. He had looked grim. She had talked in a friendly way, but not about the flat.
      </p>
<p>“They weren’t sure about the location,” Guy said breezily. “A bit on the small side as well. And they felt it needed too much
         work on it. Not keen, basically.”
      </p>
<p>“The people today shouldn’t come too late. I’m having some friends around for a drink.”</p>
<p>“Birthday, is it?”</p>
<p>I took a deep breath.</p>
<p>“Do you really want to know, Guy?”</p>
<p>“Well…”</p>
<p>“I’m having an anniversary party because it’s six months since this flat went on the market.”</p>
<p>“It’s not, is it?”</p>
<p>“Yes, it is.”</p>
<p>“It doesn’t seem like six months.”</p>
<p>He took some convincing. After the call I looked around the room rather desperately. Strangers were going to be coming in
         and looking at this room. When I moved to London my aunt had given me a book on Household Hints and Handy Tips. It had advice
         on how to tidy if you’ve only got fifteen minutes. But what if you’ve only got one minute? I made my bed, straightened the
         rug by the door, rinsed my coffee mug and put it neatly upside down by the sink. I found a cardboard box in a cupboard and
         I tipped all the letters into it and stowed it under my bed. A minute and a half and I was late at the school. Again. Sweatily
         late and the day was only starting to get hot.
      </p>
<p class="orn">* * *</p>
<p class="spb">“So, my love, what can we do to make this more salable?”</p>
<p>Louise was standing at the window with a bottle of beer, brandishing a cigarette at Holloway Road.</p>
<p>“It’s very simple,” I said. “Get rid of the road. Get rid of the pub next door and the kebab house next to that. Decorate.
         It’s horrible, isn’t it; everything about it. I hated it from the moment I owned it and even if it means losing money I’ve
         got to get out. I want to rent a small cozy flat with a garden or something. We’re meant to be in the middle of a housing
         boom. There must be somebody mad out there.” I took a drag of my cigarette. “Granted, lots of mad people have already looked
         at this flat. I need the right kind of mad person.”
      </p>
<p>Louise laughed. She had come early to help me get things ready and to have a proper talk and basically because she’s a good
         person.
      </p>
<p>“But I didn’t come all the way here to talk about property. I want to know about this new man. Is he coming tonight?”</p>
<p>“They’re all coming.”</p>
<p>“What do you mean <em>all?</em> Have you got more than one?”
      </p>
<p>I giggled.</p>
<p>“No. He goes round with this gang of boys. I think they’ve all known each other since primary school or something ridiculous.
         They’re like those six-packs of beer. You know, not to be sold separately.”
      </p>
<p>Louise frowned.</p>
<p>“This isn’t some sort of strange five-in-a-bed sex thing, is it? If so, I want to hear about it in detail.”</p>
<p>“No, they leave us alone some of the time.”</p>
<p>“How did you meet?”</p>
<p>I lit another cigarette.</p>
<p>“I met them all together. A few weeks ago I went to a party at a gallery over in Shoreditch. It was one of those typical disasters.
         The person I knew turned out not to be there. So I wandered from room to room holding a drink and pretending to be on my way
         to somewhere important. You know what I mean?”
      </p>
<p>“You’re talking to the world champion,” said Louise.</p>
<p>“Anyway I went upstairs and there was a group of good-looking young men round a pinball machine, banging at it, shouting,
         laughing, having a better time than anyone else there. One of them—not Fred, as it happens—looked round and asked me if I
         wanted to play. So I did. We had a great time and the next evening I met them again in town.”
      </p>
<p>Louise looked thoughtful.</p>
<p>“So you faced the difficult choice of which one of them actually to go out with on a one-to-one basis?”</p>
<p>“It wasn’t exactly like that,” I said. “The day after <em>that</em> Fred rang me at home and asked me out. I asked him if he had the permission of his gang, and he was a bit sheepish about
         that.” I leaned out the window a bit. “And here they are now.”
      </p>
<p>Louise peered out. They were some way down the road and hadn’t noticed us.</p>
<p>“They look like nice boys,” she said primly.</p>
<p>“That’s Fred in the middle carrying the large bag, the one with very light brown hair, almost blond.”</p>
<p>“So you grabbed the nicest-looking one.”</p>
<p>“The one in the very long coat, that’s Duncan.”</p>
<p>“How can he wear it in this heat?”</p>
<p>“Apparently it makes him look like a gunman in some spaghetti western. He never takes it off. The other two are brothers.
         The Burnside brothers. The one in the glasses and the cap is Graham. The one with the long hair is Morris. Hi!” This last
         was yelled down at them.
      </p>
<p>They looked up, startled for a moment.</p>
<p>“We’d love to come up,” Duncan shouted. “Unfortunately we’ve got to go to a party.”</p>
<p>“Shut up,” I said. “Here, catch.”</p>
<p>I dropped my bunch of keys and, with what I have to say was remarkable style, Graham took off his cap and caught the keys
         in it. They disappeared from view as they let themselves in.
      </p>
<p>“Quick,” said Louise. “We’ve got thirty seconds. Which one of them should I marry? Who’s got the best prospects? You can leave
         out Fred for the moment.”
      </p>
<p>I thought for two seconds.</p>
<p>“Graham’s working as a photographer’s assistant.”</p>
<p>“Got it.”</p>
<p>“Duncan and Morris work together. They do all sorts of different stuff to do with computers. I don’t understand it at all,
         but then I don’t think I’m meant to. Duncan’s the life and soul of any party; Morris is rather shy when you actually get him
         on his own.”
      </p>
<p>“They’re the ones who’re brothers, right?”</p>
<p>“No, that’s Morris and Graham. Duncan has red hair. He looks completely different.”</p>
<p>“All right. So for the moment the computer people seem the better bet. Morris the shy brother and Duncan the talkative redhead.”</p>
<p>And then they were in the room, filling it. When I’d talked to them about this event they had been asking brashly what sort
         of women would be there and they had been noisy down on the street, but in my flat they went a bit quiet and polite as they
         were introduced to Louise. That was something I liked about them, in a way.
      </p>
<p>Fred came over and gave me a lingering kiss, which I couldn’t help thinking was a public demonstration to everyone in the
         room. Was he showing affection or marking out territory? Then he produced something that looked like a brightly colored drape.
      </p>
<p>“I thought this would be helpful. It’s to hang over the damp patch,” he said.</p>
<p>“Thanks, Fred.” I looked at it dubiously. It was a bit bright; its colors clashed. “But I think that surveyors are allowed
         to move bits of cloth out of the way to see what’s behind them.”
      </p>
<p>“Let’s get you to the surveyor stage first. Hang it up then.”</p>
<p>“Oh. Okay.”</p>
<p>“Zoe says you’re geniuses with computers,” Louise was saying to Duncan.</p>
<p>Morris, who was standing with us, blushed slightly, which was sweet.</p>
<p>“She’d think so,” Duncan said, pulling the ring top off a can of beer. “But she’s got low standards. That’s just because we
         showed her how to use her own computer.” He took a sip. “Admittedly that was an impressive achievement. It was like teaching
         a squirrel how to find nuts.”
      </p>
<p>“But squirrels are brilliant at finding nuts,” Morris objected.</p>
<p>“That’s right,” Duncan said.</p>
<p>“But they’re good already,” Morris persisted.</p>
<p>“That’s right. Zoe is now as good with her computer as a squirrel is at finding nuts.”</p>
<p>“But you should have said it was like teaching a squirrel how to juggle.”</p>
<p>Duncan looked puzzled.</p>
<p>“But you can’t teach squirrels how to juggle.”</p>
<p>I topped up Louise’s drink.</p>
<p>“They can go on like this for hours,” I said. “It’s a bonding thing. Something to do with having been on the playground together.”</p>
<p>I went off to get some crisps from the kitchen and Louise came with me. We could see the boys in the room.</p>
<p>“He’s lovely-looking,” she said, nodding over at Fred. “What’s he smoking? He looks very relaxed. Exotic.”</p>
<p>“He’s got a hippie side to him. Relaxed is good, though.”</p>
<p>“Is it serious?”</p>
<p>I took a sip from her glass.</p>
<p>“I’ll have to get back to you about that,” I said.</p>
<p>A few other people arrived. There was John, a nice teacher from our school, who had asked me out just a few days too late.
         And a couple of women I had met through Louise. It had turned into a real miniature celebration. After a couple of drinks
         I was starting to feel benevolent toward them, this new circle of people. All they had in common was me. A year ago I was
         lonely and lost and I hadn’t met a single one of them, and now they were all coming to my so-called home on a Friday evening.
         Suddenly there was a chinking sound. Fred was rapping on a glass bottle with a fork.
      </p>
<p>“Silence, silence,” he said when there already was silence. “Unaccustomed as I am, et cetera. I’d just like to stand up and
         be counted and say that I <em>like</em> this flat and I’d like us all to raise our glasses and hope that we’ll all be able to meet here again in six months’ time
         and have another good evening.” There was a general raising of glasses and bottles. A flash went off in my face as Graham
         took a photograph. He was always doing that—you’d be chatting away to him and he’d suddenly lift up his camera and aim it
         at you, like a third eye. It could be quite disconcerting, as if all the time he was talking or listening to you, he was really
         in search of a good shot. “Also,” continued Fred, “it’s also our anniversary.” There were starts of surprise all round, not
         least from me. “Yes,” he said, “it’s nine days since Zoe and me first… erm…” There was a pause. “Er… met.” There were some
         suppressed laughs behind me from Duncan and Graham, but not from anybody else. I felt for a moment as if I were trapped at
         a rugby club dinner.
      </p>
<p>“Fred,” I said, but he held up a hand to stop me.</p>
<p>“Hang on,” he said. “It would be sad if such an evening were not marked in a solemn way but… what’s this?” He said this last
         in a pathetically false tone of amazement as he bent down and rummaged behind my armchair. He pulled out a large parcel wrapped
         in brown paper. “Either this is another offering from one of Zoe’s anonymous fans or else it must be a present.”
      </p>
<p>“Idiots,” I said, but in a nice way. It looked like a picture. I ripped off the paper and then saw what it was. “You bastards,”
         I said, laughing. It was an entire framed page of the <em>Sun</em> featuring the headline <span class="small">ME AND MY MELON</span> and in smaller writing: <span class="small">HAVE-A-GO BLONDE ZAPS MUGGER.</span></p>
<p>“Speech,” said Louise through cupped hands. “Speech.”</p>
<p>“Well,” I began, before I was interrupted by the ring of the doorbell. “Wait,” I said. “One minute.”</p>
<p>I opened the door to find a man dressed in a brown corduroy suit and rubber boots.</p>
<p>“I’ve come to see the flat,” he said. “Is that all right?”</p>
<p>“Yes, yes,” I said eagerly. “Come on up.”</p>
<p>As I led him up the stairs, the talking of the guests became audible.</p>
<p>“You seem to be having a party,” he said.</p>
<p>“Yes,” I said. “It’s my birthday.”</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// BENEATHTHESKIN_chap-3.vue?306653ae